import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Eye, EyeSlash } from '@phosphor-icons/react';
import { useAuth } from '../../contexts/AuthContext';
import { HaleWordmark } from '../../components/HaleBrand';
import TI from '../../components/brand/TI';
import HaleHead from '../../components/hale/HaleHead';
import { T, FONT } from './_theme';

/* /hale/register — fully white-labeled Hale account creation.
 * Reuses POST /api/auth/register (creates a role:"brand" user + session).
 * On success the user is redirected straight into /hale/dashboard —
 * never through ToxIntel screens. No duplicate auth/user system. */

const inputCls = 'w-full h-11 px-3.5 rounded-lg border text-[14px] outline-none transition-colors focus:border-[#E63946]';
const inputStyle = { borderColor: T.LINE, backgroundColor: T.WHITE, color: T.INK, fontFamily: FONT.BODY };

export default function HaleRegister() {
  const { register, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [f, setF] = useState({ company: '', first: '', last: '', email: '', password: '', confirm: '' });
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!authLoading && user) navigate('/hale/dashboard', { replace: true });
  }, [authLoading, user, navigate]);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (f.password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (f.password !== f.confirm) { setError('Passwords do not match.'); return; }
    setBusy(true);
    const name = `${f.first.trim()} ${f.last.trim()}`.trim();
    const r = await register(name, f.email.trim(), f.password, f.company.trim());
    if (r.success) navigate('/hale/dashboard', { replace: true });
    else setError(r.error || 'Could not create your account.');
    setBusy(false);
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: T.WHITE, color: T.INK, fontFamily: FONT.BODY }}>
      <HaleHead title="Hale Brand Portal" />
      <header className="px-5 sm:px-8 h-16 flex items-center border-b" style={{ borderColor: T.LINE }}>
        <Link to="/" aria-label="Hale home"><HaleWordmark size="sm" /></Link>
      </header>

      <main className="flex-1 flex items-center justify-center px-5 py-12">
        <div className="w-full max-w-lg">
          <div className="mb-8">
            <p className="text-[10px] uppercase tracking-[0.24em] mb-3" style={{ color: T.RED, fontFamily: FONT.MONO }}>The Hale League</p>
            <h1 className="text-[clamp(1.9rem,4vw,2.6rem)] font-black tracking-[-0.02em] leading-[1.05]" style={{ fontFamily: FONT.DISPLAY, color: T.INK }} data-testid="hale-register-title">
              Create your brand account
            </h1>
            <p className="mt-3 text-[14px] leading-relaxed" style={{ color: T.MUTED }}>
              Set up access to your Hale brand portal — applications, scientific review, documents, and marketplace resources.
            </p>
          </div>

          {error && (
            <div className="mb-5 px-4 py-3 rounded-lg text-[13px]" style={{ backgroundColor: 'rgba(185,28,28,0.06)', color: '#B91C1C', border: '1px solid rgba(185,28,28,0.25)' }} data-testid="hale-register-error">
              {error}
            </div>
          )}

          <form onSubmit={submit} className="space-y-4" data-testid="hale-register-form">
            <div>
              <label className="block text-[12px] font-semibold mb-1.5" style={{ color: T.INK }}>Company Name</label>
              <input required value={f.company} onChange={set('company')} className={inputCls} style={inputStyle} placeholder="Your brand / company" data-testid="hale-register-company" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[12px] font-semibold mb-1.5" style={{ color: T.INK }}>First Name</label>
                <input required value={f.first} onChange={set('first')} className={inputCls} style={inputStyle} data-testid="hale-register-first" />
              </div>
              <div>
                <label className="block text-[12px] font-semibold mb-1.5" style={{ color: T.INK }}>Last Name</label>
                <input required value={f.last} onChange={set('last')} className={inputCls} style={inputStyle} data-testid="hale-register-last" />
              </div>
            </div>
            <div>
              <label className="block text-[12px] font-semibold mb-1.5" style={{ color: T.INK }}>Email</label>
              <input type="email" required value={f.email} onChange={set('email')} className={inputCls} style={inputStyle} placeholder="you@brand.com" data-testid="hale-register-email" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[12px] font-semibold mb-1.5" style={{ color: T.INK }}>Password</label>
                <div className="relative">
                  <input type={showPw ? 'text' : 'password'} required value={f.password} onChange={set('password')} className={inputCls} style={inputStyle} placeholder="8+ characters" data-testid="hale-register-password" />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: T.MUTED }} tabIndex={-1}>
                    {showPw ? <EyeSlash size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-[12px] font-semibold mb-1.5" style={{ color: T.INK }}>Confirm Password</label>
                <input type={showPw ? 'text' : 'password'} required value={f.confirm} onChange={set('confirm')} className={inputCls} style={inputStyle} placeholder="Re-enter password" data-testid="hale-register-confirm" />
              </div>
            </div>
            <button type="submit" disabled={busy} className="w-full h-11 rounded-lg text-[13px] font-semibold tracking-wide inline-flex items-center justify-center gap-2 disabled:opacity-60" style={{ backgroundColor: T.RED, color: T.WHITE }} data-testid="hale-register-submit">
              {busy ? 'Creating account…' : <>Create Account <ArrowRight size={15} weight="bold" /></>}
            </button>
          </form>

          <p className="mt-6 text-[13px]" style={{ color: T.MUTED }}>
            Already have an account?{' '}
            <Link to="/hale/login" className="font-semibold" style={{ color: T.RED }} data-testid="hale-register-signin-link">Sign In</Link>
          </p>

          <p className="mt-8 text-[10px] uppercase tracking-[0.18em]" style={{ color: T.MUTED, fontFamily: FONT.MONO }}>
            Scientific review powered by <TI color={T.PINK} /> + Aᴳ
          </p>
        </div>
      </main>
    </div>
  );
}
